// ── Lead attribution ──────────────────────────────────────────────────────────
// Where a visit came from, carried to every lead form so "which articles,
// boards and campaigns produce leads" is a query, not a guess. Grew out of
// articleOrigin.js (the last article read), which it now contains.
//
// What is kept, and where:
//   sessionStorage pl_attribution — this tab's visit on this host:
//     landing_path  first path of the visit (no query: it can carry tokens)
//     referrer      the OUTSIDE page that sent it (origin + path, no query)
//     utm_*         the first campaign tags seen in the visit. An outside
//                   campaign is never overwritten by our own article CTA tags,
//                   so a Google Ads click that reads an article and then enquires
//                   is still credited to the ad; the article is recorded below.
//   localStorage pl_article_origin — the last article read, for 7 days (the key
//     articleOrigin.js used, so visitors mid-journey keep theirs).
//
// The server (supabase/functions/_shared/attribution.ts) validates and clips
// every value; nothing here is trusted.

const SESSION_KEY = 'pl_attribution';
const ARTICLE_KEY = 'pl_article_origin';
const ARTICLE_TTL_MS = 7 * 24 * 3600 * 1000;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
const OWN_HOSTS = /(^|\.)(parcelink\.org|runmark\.co\.uk|localhost)$/i;
const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

// The tags every article CTA carries (owner-directed, growth step 3).
export const ARTICLE_CTA_UTM = { utm_source: 'blog', utm_medium: 'article' };
export const isArticleCtaUtm = (p) =>
  p?.get?.('utm_source') === ARTICLE_CTA_UTM.utm_source && p?.get?.('utm_medium') === ARTICLE_CTA_UTM.utm_medium;

const clip = (v, n) => {
  const s = v == null ? '' : String(v).trim();
  return s ? s.slice(0, n) : null;
};

const readSession = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; }
};

// An outside page only: our own hosts navigating between each other are not a source.
const outsideReferrer = (ref) => {
  try {
    if (!ref) return null;
    const u = new URL(ref);
    if (!/^https?:$/.test(u.protocol) || OWN_HOSTS.test(u.hostname)) return null;
    return clip(`${u.origin}${u.pathname}`, 300);
  } catch { return null; }
};

// ── AI assistants (growth step 7) ─────────────────────────────────────────────
// A visit whose referrer (or utm_source: ChatGPT adds utm_source=chatgpt.com)
// is an AI assistant fires GA4 `ai_referral` once, with `ai_source`. The same
// list is public.ai_source_of() in migration 20261311, which classifies leads.
export const AI_REFERRERS = [
  ['chatgpt', /(^|[./])(chatgpt\.com|chat\.openai\.com|openai\.com)/],
  ['perplexity', /(^|[./])perplexity\.ai/],
  ['gemini', /(^|[./])gemini\.google\.com/],
  ['copilot', /(^|[./])copilot\.microsoft\.com/],
  ['claude', /(^|[./])claude\.ai/],
  ['you', /(^|[./])you\.com/],
];
export const aiSourceOf = (referrer, utmSource) => {
  const s = `${utmSource || ''} ${referrer || ''}`.toLowerCase();
  return AI_REFERRERS.find(([, re]) => re.test(s))?.[0] || null;
};

// ── Article origin (was articleOrigin.js) ─────────────────────────────────────
export const captureArticleOrigin = (slug) => {
  if (!SLUG_RE.test(String(slug || ''))) return;
  try { localStorage.setItem(ARTICLE_KEY, JSON.stringify({ slug, at: Date.now() })); } catch { /* private mode */ }
};

const readArticle = () => {
  try {
    const v = JSON.parse(localStorage.getItem(ARTICLE_KEY) || 'null');
    return v?.slug && Date.now() - v.at < ARTICLE_TTL_MS ? v : null;
  } catch { return null; }
};

export const articleOriginFields = () => {
  const v = readArticle();
  return v ? { origin_article: v.slug, origin_article_at: new Date(v.at).toISOString() } : {};
};

// ── The visit ─────────────────────────────────────────────────────────────────
/** Record this page view. Safe to call on every route change. */
export function captureAttribution(loc = typeof window !== 'undefined' ? window.location : null,
  referrer = typeof document !== 'undefined' ? document.referrer : '') {
  if (!loc) return;
  try {
    const params = new URLSearchParams(loc.search || '');
    const prev = readSession();
    const next = prev || {
      landing_path: clip(loc.pathname || '/', 300),
      referrer: outsideReferrer(referrer),
      at: Date.now(),
    };
    const tagged = UTM_KEYS.some((k) => params.get(k));
    if (tagged && !UTM_KEYS.some((k) => next[k])) {
      UTM_KEYS.forEach((k) => { next[k] = clip(params.get(k), k === 'utm_content' ? 200 : 120); });
    }
    // An article CTA names its article in utm_content.
    if (isArticleCtaUtm(params)) captureArticleOrigin(params.get('utm_content'));
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    // First page of the visit only, so a visit is one ai_referral however far it reads.
    const ai = !prev && aiSourceOf(next.referrer, params.get('utm_source'));
    if (ai) gtagEvent('ai_referral', { ai_source: ai, landing_path: next.landing_path });
  } catch { /* storage unavailable — attribution is best-effort */ }
}

/** The body every lead endpoint takes as `attribution`. */
export function attributionPayload() {
  const s = readSession() || {};
  const a = readArticle();
  const out = {
    utm_source: s.utm_source || null,
    utm_medium: s.utm_medium || null,
    utm_campaign: s.utm_campaign || null,
    utm_content: s.utm_content || null,
    referrer: s.referrer || null,
    landing_path: s.landing_path || null,
    article_slug: a?.slug || null,
  };
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v));
}

/** For candidate_applications.onboarding_responses: the old article keys plus the visit. */
export const attributionFields = () => ({ ...articleOriginFields(), attribution: attributionPayload() });

/** An article CTA link with its tags. Works for paths and absolute URLs. */
export function articleCtaHref(href, slug) {
  if (!SLUG_RE.test(String(slug || ''))) return href;
  const q = new URLSearchParams({ ...ARTICLE_CTA_UTM, utm_content: slug }).toString();
  const [base, hash = ''] = String(href).split('#');
  return `${base}${base.includes('?') ? '&' : '?'}${q}${hash ? `#${hash}` : ''}`;
}

// ── GA4: form_start and generate_lead ─────────────────────────────────────────
// Parameters are the visit, never the person: no name, email or phone.
export const leadParams = (form) => {
  const p = attributionPayload();
  return Object.fromEntries(Object.entries({
    form_name: form,
    article_slug: p.article_slug,
    lead_utm_source: p.utm_source,
    lead_utm_medium: p.utm_medium,
    lead_utm_campaign: p.utm_campaign,
  }).filter(([, v]) => v != null));
};

const gtagEvent = (name, params) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
};

/** form_start, once per form per visit. */
export function trackFormStart(form) {
  if (!form) return;
  try {
    const k = `pl_form_start:${form}`;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, '1');
  } catch { /* still send: a duplicate beats a missing start */ }
  gtagEvent('form_start', leadParams(form));
}

/** generate_lead, on a lead actually saved. Candidates use trackConversionOnce(…, leadParams(form)) so a reload cannot count twice. */
export const trackLead = (form) => gtagEvent('generate_lead', leadParams(form));

/**
 * One listener for every lead form: the first focus inside an element marked
 * data-lead-form="<name>" sends form_start for that form.
 */
let listening = false;
export function listenForFormStarts() {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  document.addEventListener('focusin', (e) => {
    const el = e.target?.closest?.('[data-lead-form]');
    if (el) trackFormStart(el.getAttribute('data-lead-form'));
  });
}
